import type { TransferLineRow } from '../../data/transfers';

export type TransferLineIdentityDraft = {
  kind: 'external' | 'accessory' | 'item' | 'product' | 'asset' | 'vehicle';
  reference: string;
  name: string;
  qty: number;
};

export type TransferLinePhysicalGuard = {
  locked: boolean;
  protectedQuantity: number;
  message: string | null;
};

export const TRANSFER_LINE_PHYSICAL_LOCK_MESSAGE =
  'Picked or moved inventory cannot be deleted, reduced, or changed to another item.';

const PHYSICAL_LINE_STATUSES = new Set([
  'partially_picked',
  'picked',
  'partially_shipped',
  'shipped',
  'in_transit',
  'partially_received',
  'received',
  'kept',
]);

const FULL_QUANTITY_LINE_STATUSES = new Set([
  'picked',
  'shipped',
  'in_transit',
  'received',
  'kept',
]);

const SERIAL_ASSIGNMENT_KEYS = [
  'transfer_line_serials',
  'serial_assignments',
  'assigned_serials',
  'serials',
] as const;

function serialProtectedQuantity(value: unknown): number {
  if (Array.isArray(value)) {
    return value.reduce(
      (total, child) => total + serialProtectedQuantity(child),
      0,
    );
  }
  if (!value || typeof value !== 'object') return 0;

  const record = value as Record<string, unknown>;
  const status = String(record.status || '').trim().toLowerCase();
  const movementQuantity = Math.max(
    Number(record.shipped_qty) || 0,
    Number(record.received_qty) || 0,
    Number(record.kept_qty) || 0,
  );
  const directQuantity = Math.max(
    movementQuantity,
    PHYSICAL_LINE_STATUSES.has(status)
      || Boolean(record.picked_at || record.shipped_at || record.received_at)
      ? 1
      : 0,
  );
  return directQuantity + serialProtectedQuantity(record.data);
}

function loadedSerialProtectedQuantity(line: TransferLineRow): number {
  const record = line as unknown as Record<string, unknown>;
  return Math.max(
    0,
    ...SERIAL_ASSIGNMENT_KEYS.map(
      (key) => serialProtectedQuantity(record[key]),
    ),
  );
}

export function getTransferLinePhysicalGuard(
  line: TransferLineRow,
): TransferLinePhysicalGuard {
  const status = String(line.status || '').trim().toLowerCase();
  const requestedQuantity = Math.max(0, Number(line.qty) || 0);
  const movementQuantity = Math.max(
    Number(line.shipped_qty) || 0,
    Number(line.received_qty) || 0,
    Number(line.kept_qty) || 0,
  );
  const quantityKnownToBePhysical = Math.max(
    movementQuantity,
    loadedSerialProtectedQuantity(line),
  );
  const fullQuantityEvidence =
    FULL_QUANTITY_LINE_STATUSES.has(status)
    || Boolean(line.picked_at || line.shipped_at || line.received_at);
  const hasUnknownPartialEvidence =
    PHYSICAL_LINE_STATUSES.has(status)
    && quantityKnownToBePhysical === 0;
  const protectedQuantity = Math.max(
    quantityKnownToBePhysical,
    fullQuantityEvidence || hasUnknownPartialEvidence
      ? requestedQuantity
      : 0,
  );
  const hasPhysicalEvidence = protectedQuantity > 0;

  return {
    locked: hasPhysicalEvidence,
    protectedQuantity,
    message: hasPhysicalEvidence ? TRANSFER_LINE_PHYSICAL_LOCK_MESSAGE : null,
  };
}

function normaliseIdentityText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function getTransferLineIdentityKind(
  line: TransferLineRow,
): TransferLineIdentityDraft['kind'] {
  if (line.external_item_name?.trim()) {
    return line.external_item_type === 'accessory' ? 'accessory' : 'external';
  }
  if (line.asset_id || line.asset?.asset_id) return 'asset';
  if (line.vehicle_id) return 'vehicle';
  if (line.item_id) return 'item';
  if (line.product_id) return 'product';
  return 'external';
}

function identityChanged(
  line: TransferLineRow,
  draft: TransferLineIdentityDraft,
): boolean {
  const originalKind = getTransferLineIdentityKind(line);
  if (draft.kind !== originalKind) return true;

  switch (originalKind) {
    case 'item':
      return normaliseIdentityText(draft.reference)
        !== normaliseIdentityText(line.item_id);
    case 'product':
      return normaliseIdentityText(draft.reference)
        !== normaliseIdentityText(line.product_id);
    case 'external':
    case 'accessory':
      return normaliseIdentityText(draft.name)
        !== normaliseIdentityText(line.external_item_name);
    case 'asset':
    case 'vehicle':
      return false;
  }
}

export function getTransferLineEditViolation(
  line: TransferLineRow,
  draft: TransferLineIdentityDraft,
): string | null {
  const guard = getTransferLinePhysicalGuard(line);
  if (!guard.locked) return null;

  if (
    Number(draft.qty) < guard.protectedQuantity
    || identityChanged(line, draft)
  ) {
    return guard.message;
  }
  return null;
}
